<script setup lang="ts">
import {computed, onMounted, reactive, ref} from "vue";
import PageLabel from "components/common/PageLabel.vue";
import {VideoService} from "@/api/api.ts";
import {ElMessageBox, ElNotification} from "element-plus";
import {MyFileUploader, RecordVideo, UploadParam} from "@/types";
import {getDateInfo, getDurationStr, getTimeStr} from "@/utils/common.ts";
import AddRecordVideo from "components/dialog/record/AddRecordVideo.vue";
import UploadListDialog from "components/dialog/record/UploadListDialog.vue";
import {QuestionFilled} from "@element-plus/icons-vue";
import RecordVideoPlayDialog from "components/dialog/record/RecordVideoPlayDialog.vue";

const recordVideoPlayDialog = ref<InstanceType<typeof RecordVideoPlayDialog>>()
const buttonRef = ref()
const popoverRef = ref()
const uploaderList = ref<MyFileUploader[]>()
const addRecordVideo = ref<InstanceType<typeof AddRecordVideo>>()
const uploadListDialog = ref<InstanceType<typeof UploadListDialog>>()
const multipleSelection = ref<RecordVideo[]>([])
const content = ref('')
const loading = ref(false)
const videoList = ref<RecordVideo[]>()
const pagination = reactive({
  current: 1,
  pageSize: 10,
  total: 0
})
const range = ref<Date[]>([])
const shortcuts = [
  {
    text: '过去7天',
    value: () => {
      const end = new Date()
      const start = new Date()
      start.setDate(start.getDate() - 7)
      return [start, end]
    },
  },
  {
    text: '过去一个月',
    value: () => {
      const end = new Date()
      const start = new Date()
      start.setMonth(start.getMonth() - 1)
      return [start, end]
    },
  },
  {
    text: '过去三个月',
    value: () => {
      const end = new Date()
      const start = new Date()
      start.setMonth(start.getMonth() - 3)
      return [start, end]
    },
  },
  {
    text: '过去一年',
    value: () => {
      const end = new Date()
      const start = new Date()
      start.setFullYear(start.getFullYear() - 1)
      return [start, end]
    },
  },
]

const getVideoList = async () => {
  try {
    loading.value = true
    const {start_time, end_time} = getDateInfo(range.value)
    const res = await VideoService.getVideoList({
      page: pagination.current,
      pageSize: pagination.pageSize,
      content: content.value,
      start_time: start_time,
      end_time: end_time,
    })
    if (res.data.code === 200) {
      pagination.total = res.data.data?.count || 0
      pagination.current = res.data.data?.page || 1
      videoList.value = res.data.data?.list
    } else {
      ElNotification.error({
        title: '获取视频列表失败',
        message: res.data.msg,
        type: 'error',
      })
    }
  } catch (err) {
    console.warn(err)
  } finally {
    loading.value = false
  }
}

const onPlay = (videoId: string) => {
  recordVideoPlayDialog.value?.show(videoId,'1')
}

const onBatchDel = async () => {
  const ids = multipleSelection.value.map(item => item.id)
  if (ids.length === 0) {
    return ElNotification.warning({
      title: '提示',
      message: '请勾选需要删除的视频',
      type: 'warning',
    })
  }
  ElMessageBox.confirm(
      '确认删除选中视频吗?删除前请自行确认这些视频没有使用，否则将导致对应的课程视频无法正常播放！！',
      '注意',
      {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning',
      }
  )
      .then(() => {
        return VideoService.delVideo({
          id: ids.join(',').toString(),
        })
      })
      .then(res => {
        if (res.data.code === 200) {
          getVideoList()
          ElNotification.success({
            title: '删除成功',
            message: res.data.msg,
            type: 'success',
          })
        } else {
          ElNotification.error({
            title: '删除视频失败',
            message: res.data.msg,
            type: 'error',
          })
        }
      })
      .catch(() => {
      })
}

const onDel = async (id: number) => {
  console.log(`删除${id}`)
  ElMessageBox.confirm(
      '确认删除该视频吗?删除前请自行确认该视频没有使用！！',
      '注意',
      {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning',
      }
  )
      .then(() => {
        return VideoService.delVideo({
          id: id.toString(),
        })
      })
      .then(res => {
        if (res.data.code === 200) {
          getVideoList()
          ElNotification.success({
            title: '删除成功',
            message: res.data.msg,
            type: 'success',
          })
        } else {
          ElNotification.error({
            title: '删除视频失败',
            message: res.data.msg,
            type: 'error',
          })
        }
      })
      .catch(() => {
      })
}

const onPageChange = (page: number) => {
  pagination.current = page
  getVideoList()
}

const onSizeChange = (pageSize: number) => {
  pagination.pageSize = pageSize
  getVideoList()
}

const uploadVideo = () => {
  addRecordVideo.value?.show()
}

const handleSelectionChange = (val: RecordVideo[]) => {
  multipleSelection.value = val
}

const onReset = () => {
  content.value = ''
  range.value = []
  onSearch()
}

const onSearch = () => {
  pagination.current = 1
  getVideoList()
}

const createUploader = (
    customFileUploader: MyFileUploader
) => {
  customFileUploader.uploader = new AliyunUpload.Vod({
    timeout: 60000,
    partSize: Math.round(1048576),
    parallel: 5,
    retryCount: 3,
    retryDuration: 2,
    region: import.meta.env.VITE_VOD_REGION,
    userId: "1557089946584947",
    localCheckpoint: false, //此参数是禁用服务端缓存，不影响断点续传
    // 添加文件成功
    addFileSuccess: function (uploadInfo: UploadInfo) {
      console.log("addFileSuccess: " + uploadInfo.file.name);
    },
    // 开始上传
    onUploadstarted: function (uploadInfo: UploadInfo) {
      // 如果是 UploadAuth 上传方式, 需要调用 uploader.setUploadAuthAndAddress 方法
      // 如果是 UploadAuth 上传方式, 需要根据 uploadInfo.videoId是否有值，调用点播的不同接口获取uploadauth和uploadAddress
      // 如果 uploadInfo.videoId 有值，调用刷新视频上传凭证接口，否则调用创建视频上传凭证接口
      // 如果 uploadInfo.videoId 存在, 调用 刷新视频上传凭证接口(https://help.aliyun.com/document_detail/55408.html)
      // 如果 uploadInfo.videoId 不存在,调用 获取视频上传地址和凭证接口(https://help.aliyun.com/document_detail/55407.html)
      if (!uploadInfo.videoId) {
        const uploadParams: UploadParam[] = []
        const uploadParam: UploadParam = {
          file_name: customFileUploader.file.name,
          title: customFileUploader.file.name,
          duration: customFileUploader.file.duration as number,
          width: customFileUploader.file.width as number,
          height: customFileUploader.file.height as number
        }
        uploadParams.push(uploadParam)
        VideoService.gainUploadAuth({
          video_data: JSON.stringify(uploadParams)
        }).then(res => {
          if (res.data.code === 200 && res.data.data && res.data.data.length > 0) {
            customFileUploader.uploader.setUploadAuthAndAddress(
                uploadInfo,
                res.data.data[0].UploadAuth, res.data.data[0].UploadAddress,
                res.data.data[0].VideoId,
            )
          } else {
            // 出问题
          }
        })
      } else {
        if (uploadInfo.videoId) {
          VideoService.refreshUploadAuth({
            video_id: uploadInfo.videoId,
          }).then(res => {
            if (res.data.code === 200 && res.data.data) {
              const uploadAuth = res.data.data.UploadAuth
              const uploadAddress = res.data.data.UploadAddress
              const videoId =  res.data.data.VideoId
              customFileUploader.uploader.setUploadAuthAndAddress(uploadInfo, uploadAuth, uploadAddress, videoId)
            }
          })
        }
      }
    },
    // 文件上传成功
    onUploadSucceed: function (uploadInfo: UploadInfo) {
      console.log(
          "onUploadSucceed: " +
          uploadInfo.file.name +
          ", endpoint:" +
          uploadInfo.endpoint +
          ", bucket:" +
          uploadInfo.bucket +
          ", object:" +
          uploadInfo.object
      );
    },
    // 文件上传失败
    onUploadFailed: function (uploadInfo: UploadInfo, code: number, message: string) {
      console.log(
          "onUploadFailed: file:" + uploadInfo.file.name + ",code:" + code + ", message:" + message
      );
      customFileUploader.status = 2
    },
    // 取消文件上传
    onUploadCanceled: function (uploadInfo: UploadInfo, code: number, message: string) {
      console.log(
          "Canceled file: " + uploadInfo.file.name + ", code: " + code + ", message:" + message
      );
    },
    // 文件上传进度，单位：字节, 可以在这个函数中拿到上传进度并显示在页面上
    onUploadProgress: function (uploadInfo: UploadInfo, totalSize: number, progress: number) {
      console.log(uploadInfo, totalSize, progress)
      customFileUploader.file.progress = Math.ceil(progress * 100);
    },
    // 上传凭证超时
    onUploadTokenExpired: function (uploadInfo: UploadInfo) {
      // 上传大文件超时, 如果是上传方式一即根据 UploadAuth 上传时
      // 需要根据 uploadInfo.videoId 调用刷新视频上传凭证接口(https://help.aliyun.com/document_detail/55408.html)重新获取 UploadAuth
      // 然后调用 resumeUploadWithAuth 方法, 这里是测试接口, 所以我直接获取了 UploadAuth
      if (uploadInfo.videoId) {
        VideoService.refreshUploadAuth({
          video_id: uploadInfo.videoId,
        }).then(res => {
          if (res.data.code === 200 && res.data.data) {
            const uploadAuth = res.data.data.UploadAuth
            const uploadAddress = res.data.data.UploadAddress
            const videoId =  res.data.data.VideoId
            customFileUploader.uploader.setUploadAuthAndAddress(uploadInfo, uploadAuth, uploadAddress, videoId)
          }
        })
      }
    },
    // 全部文件上传结束
    onUploadEnd: function (uploadInfo: UploadInfo) {
      console.log(uploadInfo + "onUploadEnd: uploaded all the files");
      customFileUploader.status = 1
      getVideoList()
    },
  });
  customFileUploader.uploader.addFile(customFileUploader.file, null, null, null, '{"Vod":{}}');
  customFileUploader.uploader.startUpload();
};

const startUpload = (_uploaderList: MyFileUploader[]) => {
  _uploaderList.forEach(uploader => {
    createUploader(uploader)
  })
  if (uploaderList.value && uploaderList.value.length > 0) {
    uploaderList.value.push(..._uploaderList)
  } else {
    uploaderList.value = _uploaderList
  }
  console.log(uploaderList.value)
  addRecordVideo.value?.destroy()
}

const showUploadList = () => {
  uploadListDialog.value?.show()
}

const uploadingSize = computed(() => {
  let count = 0;
  if (uploaderList.value) {
    uploaderList.value.forEach(myUploader => {
      if (myUploader.status === 0) {
        count++
      }
    })
  }
  return count
})


onMounted(() => {
  window.addEventListener("beforeunload", e => {
    if (uploadingSize.value > 0) {
      e.preventDefault();
    }
  })
})

getVideoList()
</script>

<template>
  <div class="mx-3rem mt-2.25rem">
    <page-label>
      <div class="flex items-center">
        <span class="mr-1rem">视频管理</span>
        <el-icon ref="buttonRef" class="cursor-pointer">
          <QuestionFilled/>
        </el-icon>
        <el-popover
            ref="popoverRef"
            :virtual-ref="buttonRef"
            trigger="click"
            title=""
            width="16rem"
            virtual-triggering
        >
          <div>仅用于录播课，如果提前上传视频，在创建录播课章节添加视频时可从列表选取。</div>
          <div class="font-bold mt-1rem">
            关闭或离开本页面会导致本次未上传成功的视频上传失败(本次已上传成功的视频不受影响)
          </div>
        </el-popover>
      </div>
    </page-label>

    <div class="p-1rem bg-white rounded-md mt-2rem shadow-md gap-1rem">
      <div class="flex gap-.5rem">
        <el-button type="danger" @click="onBatchDel">批量删除</el-button>
        <el-button type="primary" @click="uploadVideo">上传视频</el-button>
        <div class="ml-2rem">
          <span class="mr-.5rem text-gray-400">{{ `${uploadingSize}个视频上传中` }}</span>
          <el-button type="primary" @click="showUploadList">查看上传队列</el-button>
        </div>
      </div>
      <div class="mt-1rem ">
        <el-date-picker
            v-model="range"
            type="datetimerange"
            range-separator="至"
            start-placeholder="上传开始时间"
            end-placeholder="上传结束时间"
            :shortcuts="shortcuts"
            style="width: 20rem"
            class="mr-1rem"
        />
        <el-input v-model="content" style="width: 15rem"
                  placeholder="请输入视频名称" @change="onSearch" class="mr-1rem"/>
        <el-button type="primary" @click="onSearch">查询</el-button>
        <el-button type="danger" @click="onReset">重置</el-button>
      </div>
    </div>
    <div class="p-1rem bg-white rounded-md mt-1rem shadow-md" v-loading="loading">
      <el-table :data="videoList" @selection-change="handleSelectionChange">
        <el-table-column type="selection" width="55"/>
        <el-table-column prop="id" label="ID" width="55">
        </el-table-column>
        <el-table-column prop="video_id" label="视频ID-批量添加章节用" >
        </el-table-column>
        <el-table-column prop="file_name" label="视频名称">
        </el-table-column>
        <el-table-column prop="duration" label="时长(秒)-批量添加章节用">
          <template #default="scope">
            {{ scope.row.duration}}
          </template>
        </el-table-column>
        <el-table-column prop="duration" label="时长">
          <template #default="scope">
            {{ getDurationStr(scope.row.duration) }}
          </template>
        </el-table-column>
        <el-table-column prop="video_status" label="转码状态">
          <template #default="scope">
            {{ scope.row.video_status === 1 ? '转码成功' : '转码中' }}
          </template>
        </el-table-column>
        <el-table-column label="视频宽高">
          <template #default="scope">
            {{ `${scope.row.width} x ${scope.row.height}` }}
          </template>
        </el-table-column>
        <el-table-column label="上传时间">
          <template #default="scope">
            {{ getTimeStr(scope.row.create_time * 1000) }}
          </template>
        </el-table-column>
        <el-table-column label="转码成功时间">
          <template #default="scope">
            <span v-if="scope.row.video_status === 1">
              {{ getTimeStr(scope.row.update_time * 1000) }}
            </span>
            <span v-else>
              {{ `--` }}
            </span>
          </template>
        </el-table-column>
        <el-table-column label="操作">
          <template #default="scope">
            <el-button type="danger" @click="onDel(scope.row.id)">删除</el-button>
            <el-button type="primary" plain @click="onPlay(scope.row.video_id)" v-if="scope.row.video_status === 1">
              播放
            </el-button>
          </template>
        </el-table-column>
      </el-table>
      <el-pagination
          class="mt-1rem"
          background
          :page-sizes="[10, 20, 50, 100]"
          layout="prev, pager, next, jumper, ->, total, sizes"
          :current-page="pagination.current"
          :page-size="pagination.pageSize"
          :total="pagination.total"
          @current-change="onPageChange"
          @size-change="onSizeChange"/>
    </div>
  </div>
  <add-record-video ref="addRecordVideo" @on-start-upload="startUpload"></add-record-video>
  <upload-list-dialog ref="uploadListDialog" :uploaderList="uploaderList"/>
  <record-video-play-dialog ref="recordVideoPlayDialog"></record-video-play-dialog>
</template>

<style scoped>
</style>